import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiShield, FiRepeat, FiSearch, FiX, FiArrowRight, FiClock, FiEdit3,
  FiCheckSquare, FiFileText, FiMoreHorizontal, FiUsers, FiUserX, FiUserCheck,
  FiInbox, FiLayers, FiZap, FiRefreshCw, FiAlertCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reassignmentAPI } from '../../services/api';
import { formatDateLong } from '../../utils/dateFormat';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import { missingPermissionMessage } from '../../utils/permissions';
import PageHeader from '../../components/PageHeader/PageHeader';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import './TaskReassignment.scss';

const initials = (u) =>
  `${u?.firstName?.[0] || ''}${u?.lastName?.[0] || ''}`.toUpperCase() || '?';
const fullName = (u) => (u ? `${u.firstName} ${u.lastName}`.trim() : '');

// One side of the handover: a labelled slot that is either an empty search box
// or the chosen person. The source slot also gets the ... action, which opens
// the workload browser — you rarely know the leaver's name by heart, you know
// that *someone* is sitting on a pile of work.
function UserSlot({
  tone, icon, label, hint, selected, onSelect, activeOnly, excludeId,
  placeholder, inactiveLabel, onBrowse, browseLabel,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    const timeout = setTimeout(() => {
      reassignmentAPI.searchUsers(query, activeOnly)
        .then(({ data }) => setResults((data.users || []).filter((u) => u._id !== excludeId)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, activeOnly, excludeId]);

  return (
    <div className={`reassign-slot reassign-slot--${tone} ${selected ? 'is-filled' : ''}`}>
      <div className="reassign-slot__head">
        <span className="reassign-slot__head-icon">{icon}</span>
        <div className="reassign-slot__head-text">
          <span className="reassign-slot__label">{label}</span>
          <span className="reassign-slot__hint">{hint}</span>
        </div>
      </div>

      {selected ? (
        <div className="reassign-slot__user">
          <span className="reassign-avatar reassign-avatar--lg">{initials(selected)}</span>
          <div className="reassign-slot__user-text">
            <strong>
              {fullName(selected)}
              {selected.isActive === false && (
                <span className="reassign-pill reassign-pill--passive">{inactiveLabel}</span>
              )}
            </strong>
            <span>{selected.email}</span>
          </div>
          <button
            type="button"
            className="reassign-slot__clear"
            onClick={() => { onSelect(null); setQuery(''); }}
            title={browseLabel}
          >
            <FiX />
          </button>
        </div>
      ) : (
        <div className="reassign-slot__pick">
          <div className="reassign-slot__search">
            <FiSearch className="reassign-slot__search-icon" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
            />
            {query.length >= 2 && (results.length > 0 || !searching) && (
              <div className="reassign-slot__results">
                {results.length === 0 ? (
                  <div className="reassign-slot__results-empty">—</div>
                ) : results.map((u) => (
                  <button
                    key={u._id}
                    type="button"
                    onClick={() => { onSelect(u); setQuery(''); setResults([]); }}
                  >
                    <span className="reassign-avatar">{initials(u)}</span>
                    <div className="reassign-slot__user-text">
                      <strong>
                        {fullName(u)}
                        {u.isActive === false && (
                          <span className="reassign-pill reassign-pill--passive">{inactiveLabel}</span>
                        )}
                      </strong>
                      <span>{u.email}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {onBrowse && (
            <button
              type="button"
              className="reassign-slot__browse"
              onClick={onBrowse}
              title={browseLabel}
              aria-label={browseLabel}
            >
              <FiMoreHorizontal />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The ... browser: everyone who currently holds open work, passive users first,
// each with their live task count so you can see where the backlog actually is.
function WorkloadModal({ open, onClose, onPick, excludeId, t }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = () => {
    setLoading(true);
    reassignmentAPI.workload()
      .then(({ data }) => setRows(data.users || []))
      .catch((err) => {
        toast.error(err.response?.data?.error || t('taskReassignment.workload.failed'));
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setFilter('');
    load();
  }, [open]);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (u) => !q
      || fullName(u).toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q);
    const visible = rows.filter(match);
    return {
      passive: visible.filter((u) => u.isActive === false),
      active: visible.filter((u) => u.isActive !== false),
      total: visible.length,
    };
  }, [rows, filter]);

  if (!open) return null;

  const renderGroup = (key, icon, users) => {
    if (users.length === 0) return null;
    return (
      <div className={`reassign-workload__group reassign-workload__group--${key}`}>
        <div className="reassign-workload__group-head">
          {icon}
          <span>{t(`taskReassignment.workload.${key}Group`)}</span>
          <span className="reassign-workload__group-count">{users.length}</span>
        </div>
        <div className="reassign-workload__rows">
          {users.map((u) => {
            const isTarget = u._id === excludeId;
            return (
              <button
                key={u._id}
                type="button"
                className="reassign-workload__row"
                disabled={isTarget}
                title={isTarget ? t('taskReassignment.workload.isTarget') : undefined}
                onClick={() => { onPick(u); onClose(); }}
              >
                <span className={`reassign-avatar reassign-avatar--${u.isActive === false ? 'passive' : 'active'}`}>
                  {initials(u)}
                </span>
                <div className="reassign-workload__row-text">
                  <strong>{fullName(u)}</strong>
                  <span>{u.email}</span>
                </div>
                <span className={`reassign-pill reassign-pill--${u.isActive === false ? 'passive' : 'active'}`}>
                  {u.isActive === false ? t('taskReassignment.inactive') : t('taskReassignment.activeLabel')}
                </span>
                <span className={`reassign-workload__count ${u.isActive === false ? 'is-passive' : ''}`}>
                  <FiLayers />
                  {t('taskReassignment.workload.taskBadge', { count: u.taskCount })}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="reassign-workload__overlay" onClick={onClose}>
      <div className="reassign-workload" onClick={(e) => e.stopPropagation()}>
        <div className="reassign-workload__header">
          <span className="reassign-workload__header-icon"><FiUsers /></span>
          <div className="reassign-workload__header-text">
            <h2>{t('taskReassignment.workload.title')}</h2>
            <p>{t('taskReassignment.workload.subtitle')}</p>
          </div>
          <button
            type="button"
            className="reassign-workload__refresh"
            onClick={load}
            title={t('taskReassignment.workload.refresh')}
            disabled={loading}
          >
            <FiRefreshCw />
          </button>
          <button type="button" className="reassign-workload__close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="reassign-workload__search">
          <FiSearch />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('taskReassignment.workload.search')}
          />
        </div>

        <div className="reassign-workload__body">
          {loading ? (
            <div className="loading-spinner">{t('taskReassignment.workload.loading')}</div>
          ) : groups.total === 0 ? (
            <div className="reassign-workload__empty">
              <FiInbox />
              <h3>{t('taskReassignment.workload.emptyTitle')}</h3>
              <p>{t('taskReassignment.workload.emptyDesc')}</p>
            </div>
          ) : (
            <>
              {renderGroup('passive', <FiUserX />, groups.passive)}
              {renderGroup('active', <FiUserCheck />, groups.active)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskReassignment() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [searchParams, setSearchParams] = useSearchParams();

  const [fromUser, setFromUser] = useState(null);
  const [toUser, setToUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showWorkload, setShowWorkload] = useState(false);
  const [taskQuery, setTaskQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Merge pending approvals and pending-edit returns into one selectable list.
  const loadTasks = async (userId) => {
    setTasksLoading(true);
    try {
      const { data } = await reassignmentAPI.tasks(userId);
      const rows = [
        ...(data.approvals || []).map((a) => ({
          key: `a:${a._id}`,
          kind: 'approval',
          id: a._id,
          contract: a.contractId,
          stepName: a.stepName,
          stepType: a.stepSystemKey === 'signed_contract' ? 'signature' : (a.stepType || 'approval'),
          dueAt: a.dueAt,
          isCurrentStep: a.isCurrentStep,
        })),
        ...(data.editContracts || []).map((c) => ({
          key: `e:${c._id}`,
          kind: 'edit',
          id: c._id,
          contract: c,
          stepName: null,
          stepType: 'edit',
          dueAt: null,
          isCurrentStep: true,
        })),
      ];
      setTasks(rows);
    } catch (err) {
      toast.error(err.response?.data?.error || t('taskReassignment.loadFailed'));
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  useEffect(() => {
    setSelectedKeys(new Set());
    setTaskQuery('');
    setTypeFilter('all');
    if (fromUser?._id) loadTasks(fromUser._id);
    else setTasks([]);
  }, [fromUser?._id]);

  // Opened from the "user deactivated with open work" notification: the source
  // user arrives as ?userId=. Resolve them once, then drop the param so a later
  // manual pick is not undone by a refresh.
  const presetUserId = searchParams.get('userId');
  useEffect(() => {
    if (!presetUserId) return;
    let cancelled = false;
    reassignmentAPI.getUser(presetUserId)
      .then(({ data }) => {
        const preset = (data.users || [])[0];
        if (!cancelled && preset) setFromUser(preset);
      })
      .catch(() => { /* fall back to picking manually */ })
      .finally(() => {
        if (!cancelled) setSearchParams({}, { replace: true });
      });
    return () => { cancelled = true; };
  }, [presetUserId]);

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const row of tasks) counts[row.stepType] = (counts[row.stepType] || 0) + 1;
    return counts;
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    const q = taskQuery.trim().toLowerCase();
    return tasks.filter((row) => {
      if (typeFilter !== 'all' && row.stepType !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        row.contract?.contractName,
        row.contract?.contractNumber,
        row.contract?.companyId?.name,
        row.stepName && localizeSeeded(row.stepName, lang),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [tasks, taskQuery, typeFilter, lang]);

  // Select-all acts on what is on screen, so a filter narrows the batch too.
  const allVisibleSelected = visibleTasks.length > 0
    && visibleTasks.every((row) => selectedKeys.has(row.key));
  const toggleAll = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleTasks.forEach((row) => next.delete(row.key));
      else visibleTasks.forEach((row) => next.add(row.key));
      return next;
    });
  };
  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedCount = selectedKeys.size;
  const activeNowCount = useMemo(() => tasks.filter((r) => r.isCurrentStep).length, [tasks]);
  const canSubmit = fromUser && toUser && fromUser._id !== toUser._id
    && selectedCount > 0 && reason.trim().length > 0 && !submitting;

  const typeLabel = (type) => t(`taskReassignment.types.${type}`, { defaultValue: type });
  const stepLabel = (row) => (row.kind === 'edit'
    ? t('taskReassignment.editStep')
    : localizeSeeded(row.stepName, lang) || '—');

  const handleReassignClick = () => {
    if (!fromUser || !toUser) { toast.error(t('taskReassignment.selectBothUsers')); return; }
    if (fromUser._id === toUser._id) { toast.error(t('taskReassignment.sameUser')); return; }
    if (selectedCount === 0) { toast.error(t('taskReassignment.selectTasksFirst')); return; }
    if (!reason.trim()) { toast.error(t('taskReassignment.reasonRequired')); return; }
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const approvalIds = [];
      const editContractIds = [];
      for (const row of tasks) {
        if (!selectedKeys.has(row.key)) continue;
        if (row.kind === 'approval') approvalIds.push(row.id);
        else editContractIds.push(row.id);
      }
      const { data } = await reassignmentAPI.reassign({
        fromUserId: fromUser._id,
        toUserId: toUser._id,
        reason: reason.trim(),
        approvalIds,
        editContractIds,
      });
      const skippedCount = (data.skipped || []).length;
      toast.success(t('taskReassignment.success', { count: data.reassigned?.total ?? 0 }));
      if (skippedCount > 0) {
        const companyMismatch = data.skipped.some((s) => s.reason === 'COMPANY_MISMATCH');
        toast.warn(companyMismatch
          ? t('taskReassignment.skippedCompany', { count: skippedCount })
          : t('taskReassignment.skipped', { count: skippedCount }));
      }
      setConfirming(false);
      setReason('');
      setSelectedKeys(new Set());
      loadTasks(fromUser._id);
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, lang)
        || (data?.code === 'SAME_USER' ? t('taskReassignment.sameUser') : null)
        || (data?.code === 'REASON_REQUIRED' ? t('taskReassignment.reasonRequired') : null)
        || data?.error || t('taskReassignment.failed'));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const fromName = fullName(fromUser);
  const toName = fullName(toUser);

  const kindIcon = useMemo(() => ({
    approval: <FiCheckSquare />,
    edit: <FiEdit3 />,
  }), []);

  const filterChips = useMemo(() => ([
    { key: 'all', label: t('taskReassignment.filterAll'), count: tasks.length },
    ...Object.keys(typeCounts).map((type) => ({
      key: type, label: typeLabel(type), count: typeCounts[type],
    })),
  ]), [typeCounts, tasks.length, lang]);

  return (
    <div className="reassign">
      <PageHeader
        label={t('nav.accessCompliance', 'Access & Compliance')}
        labelIcon={<FiShield />}
        title={t('taskReassignment.title')}
        description={t('taskReassignment.subtitle')}
        actions={(
          <button type="button" className="btn btn-outline" onClick={() => setShowWorkload(true)}>
            <FiUsers /> {t('taskReassignment.workload.openBtn')}
          </button>
        )}
      />

      {/* ── Handover strip: who → whom ─────────────────────────────────────── */}
      <section className="reassign__handover">
        <UserSlot
          tone="from"
          icon={<FiUserX />}
          label={t('taskReassignment.fromUser')}
          hint={t('taskReassignment.fromHint')}
          selected={fromUser}
          onSelect={setFromUser}
          activeOnly={false}
          excludeId={toUser?._id}
          placeholder={t('taskReassignment.searchUser')}
          inactiveLabel={t('taskReassignment.inactive')}
          onBrowse={() => setShowWorkload(true)}
          browseLabel={t('taskReassignment.workload.openBtn')}
        />

        <div className="reassign__bridge" aria-hidden="true">
          <span className="reassign__bridge-line" />
          <span className={`reassign__bridge-badge ${selectedCount > 0 ? 'is-live' : ''}`}>
            {selectedCount > 0
              ? t('taskReassignment.workload.taskBadge', { count: selectedCount })
              : <FiArrowRight />}
          </span>
          <span className="reassign__bridge-line" />
        </div>

        <UserSlot
          tone="to"
          icon={<FiUserCheck />}
          label={t('taskReassignment.toUser')}
          hint={t('taskReassignment.toHint')}
          selected={toUser}
          onSelect={setToUser}
          activeOnly
          excludeId={fromUser?._id}
          placeholder={t('taskReassignment.searchUser')}
          inactiveLabel={t('taskReassignment.inactive')}
        />
      </section>

      {/* ── Task board ─────────────────────────────────────────────────────── */}
      <section className="reassign__board">
        {!fromUser ? (
          <div className="reassign__placeholder">
            <span className="reassign__placeholder-icon"><FiRepeat /></span>
            <h3>{t('taskReassignment.pickSourceTitle')}</h3>
            <p>{t('taskReassignment.pickSourceDesc')}</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowWorkload(true)}>
              <FiUsers /> {t('taskReassignment.workload.openBtn')}
            </button>
          </div>
        ) : tasksLoading ? (
          <div className="loading-spinner">{t('taskReassignment.loadingTasks')}</div>
        ) : tasks.length === 0 ? (
          <div className="reassign__placeholder">
            <span className="reassign__placeholder-icon reassign__placeholder-icon--ok"><FiCheckSquare /></span>
            <h3>{t('taskReassignment.noTasksTitle')}</h3>
            <p>{t('taskReassignment.noTasksDesc', { name: fromName })}</p>
          </div>
        ) : (
          <>
            <div className="reassign__stats">
              <div className="reassign__stat">
                <span className="reassign__stat-icon reassign__stat-icon--total"><FiLayers /></span>
                <div>
                  <strong>{tasks.length}</strong>
                  <span>{t('taskReassignment.statTotal')}</span>
                </div>
              </div>
              <div className="reassign__stat">
                <span className="reassign__stat-icon reassign__stat-icon--live"><FiZap /></span>
                <div>
                  <strong>{activeNowCount}</strong>
                  <span>{t('taskReassignment.statActiveNow')}</span>
                </div>
              </div>
              <div className="reassign__stat">
                <span className="reassign__stat-icon reassign__stat-icon--wait"><FiClock /></span>
                <div>
                  <strong>{tasks.length - activeNowCount}</strong>
                  <span>{t('taskReassignment.statWaiting')}</span>
                </div>
              </div>
              <div className="reassign__stat reassign__stat--selected">
                <span className="reassign__stat-icon reassign__stat-icon--sel"><FiCheckSquare /></span>
                <div>
                  <strong>{selectedCount}</strong>
                  <span>{t('taskReassignment.statSelected')}</span>
                </div>
              </div>
            </div>

            <div className="reassign__toolbar">
              <div className="reassign__chips">
                {filterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    className={`reassign__chip ${typeFilter === chip.key ? 'is-on' : ''}`}
                    onClick={() => setTypeFilter(chip.key)}
                  >
                    {chip.label}
                    <span>{chip.count}</span>
                  </button>
                ))}
              </div>
              <div className="reassign__task-search">
                <FiSearch />
                <input
                  type="text"
                  value={taskQuery}
                  onChange={(e) => setTaskQuery(e.target.value)}
                  placeholder={t('taskReassignment.searchTasks')}
                />
                {taskQuery && (
                  <button type="button" onClick={() => setTaskQuery('')}><FiX /></button>
                )}
              </div>
            </div>

            <div className="reassign__select-bar">
              <label className="reassign__check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                />
                <span>{t('taskReassignment.selectAll')}</span>
              </label>
              <span className="reassign__select-info">
                {t('taskReassignment.showingCount', {
                  shown: visibleTasks.length,
                  total: tasks.length,
                  name: fromName,
                })}
              </span>
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="reassign__clear-sel"
                  onClick={() => setSelectedKeys(new Set())}
                >
                  <FiX /> {t('taskReassignment.clearSelection')}
                </button>
              )}
            </div>

            {visibleTasks.length === 0 ? (
              <div className="reassign__placeholder reassign__placeholder--sm">
                <span className="reassign__placeholder-icon"><FiSearch /></span>
                <h3>{t('taskReassignment.noMatchTitle')}</h3>
                <p>{t('taskReassignment.noMatchDesc')}</p>
              </div>
            ) : (
              <div className="reassign__rows">
                {visibleTasks.map((row) => {
                  const on = selectedKeys.has(row.key);
                  return (
                    <div
                      key={row.key}
                      role="button"
                      tabIndex={0}
                      className={`reassign-task reassign-task--${row.stepType} ${on ? 'is-selected' : ''}`}
                      onClick={() => toggleOne(row.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOne(row.key); }
                      }}
                    >
                      <span className="reassign-task__check" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={on} onChange={() => toggleOne(row.key)} />
                      </span>

                      <span className="reassign-task__icon">
                        <FiFileText />
                      </span>

                      <div className="reassign-task__main">
                        <strong>{row.contract?.contractName || '—'}</strong>
                        <span className="reassign-task__meta">
                          {row.contract?.contractNumber && (
                            <span className="reassign-task__no">{row.contract.contractNumber}</span>
                          )}
                          {row.contract?.companyId?.name && (
                            <span>{row.contract.companyId.name}</span>
                          )}
                        </span>
                      </div>

                      <div className="reassign-task__step">
                        <span className="reassign-task__step-label">{t('taskReassignment.columns.step')}</span>
                        <span className="reassign-task__step-value">{stepLabel(row)}</span>
                      </div>

                      <span className={`reassign-task__type reassign-task__type--${row.stepType}`}>
                        {kindIcon[row.kind]} {typeLabel(row.stepType)}
                      </span>

                      <span className="reassign-task__due">
                        {row.dueAt ? (<><FiClock /> {formatDateLong(row.dueAt)}</>) : '—'}
                      </span>

                      <span className={`reassign-task__state reassign-task__state--${row.isCurrentStep ? 'active' : 'waiting'}`}>
                        {row.isCurrentStep
                          ? t('taskReassignment.stateActive')
                          : t('taskReassignment.stateWaiting')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reason + submit — sticks to the bottom while you scroll the list */}
            <div className={`reassign__action-bar ${selectedCount > 0 ? 'is-armed' : ''}`}>
              <div className="reassign__reason">
                <label htmlFor="reassign-reason">
                  {t('taskReassignment.reason')} <span className="reassign__required">*</span>
                </label>
                <input
                  id="reassign-reason"
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('taskReassignment.reasonPlaceholder')}
                />
              </div>
              <div className="reassign__action-side">
                {fromUser && toUser && (
                  <span className="reassign__route">
                    <span className="reassign-avatar reassign-avatar--xs">{initials(fromUser)}</span>
                    <FiArrowRight />
                    <span className="reassign-avatar reassign-avatar--xs">{initials(toUser)}</span>
                  </span>
                )}
                {!toUser && (
                  <span className="reassign__hint-warn"><FiAlertCircle /> {t('taskReassignment.pickTargetHint')}</span>
                )}
                <button
                  type="button"
                  className="btn btn-primary reassign__submit"
                  disabled={!canSubmit}
                  onClick={handleReassignClick}
                >
                  <FiRepeat /> {t('taskReassignment.reassignBtn', { count: selectedCount })}
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <WorkloadModal
        open={showWorkload}
        onClose={() => setShowWorkload(false)}
        onPick={setFromUser}
        excludeId={toUser?._id}
        t={t}
      />

      <ConfirmDialog
        open={confirming}
        variant="warning"
        icon={FiRepeat}
        title={t('taskReassignment.confirmTitle')}
        message={t('taskReassignment.confirmMessage', { count: selectedCount, from: fromName, to: toName })}
        confirmLabel={t('taskReassignment.confirmBtn')}
        loading={submitting}
        onConfirm={handleConfirm}
        onCancel={() => { if (!submitting) setConfirming(false); }}
      />
    </div>
  );
}

export default TaskReassignment;
